import './ReceptionsList.scss';

import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { getDefaultSortOrder, List, ShowButton, useTable } from '@refinedev/antd';
import { BaseRecord } from '@refinedev/core';
import { Flex, Space, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppTable from '@/components/general/AppTable';
import LinkTextField from '@/components/general/LinkTextField';
import PackagesListFilter from '@/components/logistic/PackagesListFilter/PackagesListFilter';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import ProtectedCreateButton from '@/components/protected/ProtectedCreateButton';
import { COMPANY_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { RECEPTION_TYPE } from '@/model/Logistic/Reception/ReceptionType';
import { RESOURCES } from '@/Resources';

export const ReceptionsList = () => {
  const { hasPermission } = usePermissionContext();
  const { pageSizePreference } = useUserContext();
  const { t } = useTranslation();
  const [isReady, setIsReady] = useState(false);
  const {
    tableProps,
    filters,
    setFilters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    sorters,
    setSorters,
  } = useTable<Reception>({
    sorters: {
      initial: [{ field: 'id', order: 'desc' }],
      mode: 'server',
    },
    syncWithLocation: true,
    queryOptions: {
      refetchInterval: 300000, // 5 minutes
      retry: 3,
      enabled: isReady,
    },
    pagination: {
      pageSize: pageSizePreference,
    },
  });

  useListStatePersistence({
    resource: RESOURCES.RECEPTIONS,
    filters,
    setFilters,
    sorters,
    setSorters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    onReady: () => setIsReady(true),
  });

  return (
    <List
      headerButtons={({ createButtonProps }) => (
        <>
          <PackagesListFilter filters={filters} setFilters={setFilters} setCurrent={setCurrent} />
          <ProtectedCreateButton {...createButtonProps} />
        </>
      )}>
      <AppTable
        {...tableProps}
        pagination={{
          ...tableProps.pagination,
          pageSizeOptions: [20, 40, 60, 80], // Opciones de cantidad por página
          showSizeChanger: true, // Muestra el selector
          showTotal: (total, range) => {
            return `${range[0]} - ${range[1]} ${t('de')} ${total}`;
          },
        }}
        rowKey="id"
        rowClassName={record => {
          return (
            'ReceptionTableRow ' +
            (record.active_claim
              ? 'ActiveClaim'
              : record.status != undefined
                ? PACKAGE_STATES[record.status]
                : '')
          );
        }}
        className="ReceptionsList"
        scroll={{ x: 1000, y: 'calc(100dvh - 310px)' }}>
        <AppTable.Column
          dataIndex="id"
          title={'Id'}
          width={65}
          sorter={{ multiple: 2 }}
          sortOrder={getDefaultSortOrder('id', sorters)}
          render={value => (
            <LinkTextField label={value} to={`/receptions/show/${value}`} local withoutColor />
          )}
        />
        <AppTable.Column
          title={t('Origen')}
          width={200}
          render={(_, record) => (
            <LinkTextField
              label={
                record.company?.full_name ? `${record.company.full_name}` : `${record.company.id}`
              }
              to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record.company.id}`}
              admin
              withoutColor
            />
          )}
        />

        <AppTable.Column
          title={t('Operador logístico')}
          width={160}
          render={(_, record) =>
            record.shipper ? (
              <LinkTextField
                label={`${record.shipper.name}`}
                to={'/shippers/show/' + record.shipper.id}
                local
                withoutColor
              />
            ) : (
              '-'
            )
          }
        />
        <AppTable.Column
          dataIndex={'has_to_be_picked_up'}
          title={t('Retiro en deposito')}
          width={160}
          render={v => (
            <div className="ReceptionsListCenteredCell">
              {v ? (
                <CheckOutlined className="ReceptionsListCheckIcon" />
              ) : (
                <CloseOutlined className="ReceptionsListCloseIcon" />
              )}
            </div>
          )}
        />
        <AppTable.Column
          width={100}
          title={t('Ticket')}
          render={(_, record) =>
            record.ticket ? (
              <LinkTextField
                label={`${record.ticket.id}`}
                to={`/tickets/show/${record.ticket.id}`}
                local
                withoutColor
              />
            ) : (
              '-'
            )
          }
        />
        <AppTable.Column
          title={t('Empresa del ticket')}
          width={240}
          render={(_, record) =>
            record?.ticket?.empresa ? (
              <LinkTextField
                label={
                  record.ticket?.empresa?.full_name
                    ? `${record.ticket.empresa.full_name}`
                    : `${record.ticket.empresa.id}`
                }
                to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record.company.id}`}
                admin
                withoutColor
              />
            ) : (
              '-'
            )
          }
        />
        <AppTable.Column
          width={150}
          dataIndex="date"
          title={t('Fecha efectiva')}
          sorter={{ multiple: 1 }}
          sortOrder={getDefaultSortOrder('date', sorters)}
          render={(_, record) => (record.date ? dayjs(record.date).format('L') : '-')}
        />
        <AppTable.Column
          width={70}
          dataIndex="tipo"
          title={t('Tipo')}
          render={(_, record) => (
            <Flex align="center" justify="center">
              {record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE ? (
                <Tooltip title={t('Recepción creada automáticamente')}>🤖</Tooltip>
              ) : (
                <Tooltip title={t('Recepción creada manualmente')}>✋🏼</Tooltip>
              )}
            </Flex>
          )}
        />
        <AppTable.Column
          width={150}
          dataIndex="created_by"
          title={t('Creado por')}
          render={(_, record) => `${record.created_by.first_name} ${record.created_by.last_name}`}
        />

        <AppTable.Column
          width={160}
          fixed="right"
          dataIndex={'status'}
          render={(_, record) => (
            <Flex vertical align="center" justify="center" gap={4}>
              {<PackageStateTag packageStatus={record.status} />}
              {record?.active_claim && <Tag color="error">{t('Reclamable')}</Tag>}
            </Flex>
          )}
        />
        {hasPermission('show') && (
          <AppTable.Column
            dataIndex="actions"
            width={55}
            fixed="right"
            render={(_, record: BaseRecord) => (
              <Space>
                <ShowButton hideText size="small" recordItemId={record.id} />
              </Space>
            )}
          />
        )}
      </AppTable>
    </List>
  );
};
